import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Badge } from "./ui/badge";
import { Card, CardContent } from "./ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { Filter, RotateCcw, AlertTriangle, Calendar, Zap, Search } from "lucide-react";
import { activeFilterClass } from "../lib/filterStyles";

const API_URL = process.env.REACT_APP_BACKEND_URL + "/api";
const ALL = "__ALL__"; // sentinel for "no filter" in Select

// Iter52 P18 — OfferFilterCombobox: search + filter by product_kind, înlocuiește
// dropdown-ul standard care era prea lung (~71 oferte amestecate EE+Gaz+VAS).
const OfferFilterCombobox = ({ offers, value, onChange, productKindFilter }) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const filtered = (offers || []).filter((o) => {
    if (productKindFilter && o.product_kind !== productKindFilter) return false;
    if (!search) return true;
    const q = search.toLowerCase();
    return (o.name || "").toLowerCase().includes(q)
      || (o.product_kind || "").toLowerCase().includes(q);
  });
  const selected = (offers || []).find((o) => o.id === value);
  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={`flex h-10 w-full items-center justify-between rounded-md border bg-white px-3 py-2 text-sm text-left ${activeFilterClass(value)}`}
        data-testid="filter-offer-combobox"
      >
        <span className={selected ? "text-slate-900" : "text-slate-500"}>
          {selected ? `${selected.name} (${selected.product_kind || "—"})` : "Toate"}
        </span>
        <Search className="h-3.5 w-3.5 text-slate-400" />
      </button>
      {open && (
        <div className="absolute z-50 mt-1 w-full bg-white border rounded-md shadow-lg max-h-72 overflow-hidden flex flex-col">
          <div className="p-2 border-b">
            <Input
              autoFocus
              type="text"
              placeholder="Caută ofertă..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="h-8 text-sm"
              data-testid="filter-offer-search"
            />
            <p className="text-[10px] text-slate-400 mt-1.5">
              {filtered.length} {filtered.length === 1 ? "ofertă" : "oferte"}
              {productKindFilter ? ` în ${productKindFilter}` : ""}
            </p>
          </div>
          <div className="overflow-y-auto flex-1">
            <button
              type="button"
              onClick={() => { onChange(""); setOpen(false); setSearch(""); }}
              className="w-full text-left px-3 py-1.5 text-sm hover:bg-slate-100"
              data-testid="filter-offer-option-all"
            >
              <span className="text-slate-500">Toate</span>
            </button>
            {filtered.map((o) => (
              <button
                key={o.id}
                type="button"
                onClick={() => { onChange(o.id); setOpen(false); setSearch(""); }}
                className={`w-full text-left px-3 py-1.5 text-sm hover:bg-slate-100 ${o.id === value ? "bg-indigo-50 text-indigo-900" : ""}`}
                data-testid={`filter-offer-option-${o.id}`}
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="truncate">{o.name}</span>
                  <Badge variant="outline" className="text-[10px] flex-shrink-0">
                    {o.product_kind || "—"}
                  </Badge>
                </div>
              </button>
            ))}
            {filtered.length === 0 && (
              <p className="text-xs text-slate-400 italic px-3 py-3 text-center">Nicio ofertă găsită</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const EMPTY = {
  status: "",
  business_status: "",
  supplier: "",
  source_system: "",
  customer_type: "",
  agency_id: "",
  agent_id: "",
  commercial_agent_id: "",  // Iter62o — agentul comercial real (cu căutare)
  product_id: "",
  offer_id: "",
  signed_date_from: "",
  signed_date_to: "",
  needs_reconciliation: "", // "true" | "false" | ""
  unassigned_only: "", // "true" — canonical: agent_ref_id IS NULL
  activation_days_min: "",
  // Iter23 — Supplier-side filters (PPC, fost ENEL)
  supplier_status: "",
  // Iter52 P18 — supplier_ok eliminat (filtru deprecat), supplier eliminat (toate sunt PPC)
  supplier_ok: "",  // "true" only — when set, filter contracts.supplier_ok=true
  csd_date_from: "",
  csd_date_to: "",
  // Iter52 P14j — SF-imported timeline filters
  document_validation_date_from: "",
  document_validation_date_to: "",
  request_confirmed_from: "",  // Iter62k — păstrat în state pt. compat (UI eliminat)
  request_confirmed_to: "",
  sf_last_modified_by: "",
  // Iter34c / Iter36 — rework + return + commission toggles
  requires_rework: "",  // "true"
  problem_reason_code: "",
  returned: "",  // "true" | "false"
  commissioned: "",  // "true" | "false"
  // Iter52 P13 — Salesforce "Stare contract" (col V): Activat | Signed | Terminated | __empty__
  sf_contract_state: "",
  // Iter52 P13d — SF Tip înregistrare caz (col J) filter + commissionable toggle
  registration_type: "",  // exact match (one of 10 canonical values)
  commissionable: "",      // "true" / "false" / ""
  // Iter52 P13h — Incomplete sales filter (missing product_kind OR offer_id)
  incomplete: "",          // "true" / "false" / ""
  // Iter53 P50 — General Status filters (Faza B UI)
  general_status: [],       // Faza B v2 — multi-select: array of statuses
  general_status_rule_code: "", // exact match on rule_code
  has_conflict: "",         // "true" / "false" / ""
  has_blocking_conflict: "", // "true" / "false" / ""
  is_stale: "",              // Faza B v2 — "true" / "false" / ""
  preset: "",                // Faza B v2 — active Quick Filter preset key
  sort_by: "created_at",
  sort_dir: "desc",
};

export const EMPTY_FILTERS = EMPTY;

const PRESETS = {
  in_activation_7d: {
    label: "În Activare > 7 zile",
    icon: Calendar,
    apply: () => ({ ...EMPTY, status: "În Activare", activation_days_min: "7" }),
    visibleFor: ["admin", "backoffice", "agentie"],
  },
  needs_reconciliation: {
    label: "Neasociate",
    icon: AlertTriangle,
    apply: () => ({ ...EMPTY, unassigned_only: "true" }),
    visibleFor: ["admin", "backoffice"],
  },
  imported_today: {
    label: "Importate azi",
    icon: Zap,
    apply: () => {
      const today = new Date().toISOString().slice(0, 10);
      return { ...EMPTY, source_system: "easy", signed_date_from: today, signed_date_to: today };
    },
    visibleFor: ["admin", "backoffice", "agentie", "agent"],
  },
};

export const AdvancedFilters = ({ value, onChange, userRole, storageKey, showActivationDays = true }) => {
  const [suppliers, setSuppliers] = useState([]);
  const [agencies, setAgencies] = useState([]);
  const [agents, setAgents] = useState([]);
  // Iter62o — agenți comerciali (listă mare → căutare + prefiltrare după agenție)
  const [commAgents, setCommAgents] = useState([]);
  const [agentSearch, setAgentSearch] = useState("");
  const [products, setProducts] = useState([]);
  const [offers, setOffers] = useState([]);
  const [statuses, setStatuses] = useState([]);

  // Load lookup data once
  useEffect(() => {
    const load = async () => {
      try {
        const [s, a, u, p, o, st, ca] = await Promise.all([
          axios.get(`${API_URL}/suppliers`).catch(() => ({ data: [] })),
          axios.get(`${API_URL}/agencies`).catch(() => ({ data: [] })),
          axios.get(`${API_URL}/users`).catch(() => ({ data: [] })),
          axios.get(`${API_URL}/products?active_only=true`).catch(() => ({ data: [] })),
          axios.get(`${API_URL}/offers?active_only=true`).catch(() => ({ data: [] })),
          axios.get(`${API_URL}/statuses`).catch(() => ({ data: [] })),
          axios.get(`${API_URL}/agents?page=1&limit=500`).catch(() => ({ data: { data: [] } })),
        ]);
        setSuppliers(s.data || []);
        setAgencies(a.data || []);
        setAgents((u.data || []).filter((x) => x.role === "agent"));
        setProducts(p.data || []);
        setOffers(o.data || []);
        setStatuses(st.data || []);
        setCommAgents((ca.data && ca.data.data) || []);
      } catch {
        // best-effort, no toast
      }
    };
    load();
  }, []);

  // Persist to sessionStorage
  useEffect(() => {
    if (storageKey) {
      try {
        sessionStorage.setItem(storageKey, JSON.stringify(value));
      } catch {
        // ignore
      }
    }
  }, [storageKey, value]);

  const setField = useCallback(
    (field, val) => {
      const normalized = val === ALL ? "" : val;
      onChange({ ...value, [field]: normalized });
    },
    [value, onChange]
  );

  const reset = () => onChange({ ...EMPTY });
  const applyPreset = (key) => onChange(PRESETS[key].apply());

  // Count active filters (excluding sort fields). Faza B v2: array values count only if non-empty.
  const activeCount = Object.entries(value).filter(([k, v]) => {
    if (["sort_by", "sort_dir"].includes(k)) return false;
    if (Array.isArray(v)) return v.length > 0;
    return Boolean(v);
  }).length;

  const showReconciliation = userRole === "admin" || userRole === "backoffice";
  const availablePresets = Object.entries(PRESETS).filter(([_, p]) =>
    p.visibleFor.includes(userRole)
  );

  return (
    <Card
      data-testid="advanced-filters"
      className={activeCount > 0 ? "border-blue-300 shadow-sm shadow-blue-100" : ""}
    >
      <CardContent className="pt-6 space-y-4">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-2 text-sm font-medium text-slate-700">
            <Filter className={`h-4 w-4 ${activeCount > 0 ? "text-blue-600" : ""}`} />
            Filtre avansate
            {activeCount > 0 && (
              <Badge
                className="bg-blue-600 hover:bg-blue-600 text-white"
                data-testid="active-filter-count"
              >
                {activeCount} {activeCount === 1 ? "activ" : "active"}
              </Badge>
            )}
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {availablePresets.map(([key, preset]) => {
              const Icon = preset.icon;
              return (
                <Button
                  key={key}
                  variant="outline"
                  size="sm"
                  onClick={() => applyPreset(key)}
                  data-testid={`preset-${key}`}
                >
                  <Icon className="h-3.5 w-3.5 mr-1.5" />
                  {preset.label}
                </Button>
              );
            })}
            <Button
              variant="ghost"
              size="sm"
              onClick={reset}
              disabled={activeCount === 0}
              data-testid="reset-filters-btn"
            >
              <RotateCcw className="h-3.5 w-3.5 mr-1.5" />
              Reset
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
          {/* Status — Iter52 P18: clarificat că e statusul intern CRM (workflow) */}
          <div>
            <Label className="text-xs" title="Statusul de workflow intern CRM (configurabil în Setări → Statusuri)">
              Status intern (workflow)
            </Label>
            <Select value={value.status || ALL} onValueChange={(v) => setField("status", v)}>
              <SelectTrigger data-testid="filter-status" className={activeFilterClass(value.status)}>
                <SelectValue placeholder="Toate" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>Toate</SelectItem>
                {statuses.map((s) => (
                  <SelectItem key={s.id || s.name} value={s.name}>
                    {s.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Business status — canonical, derived from sources */}
          {/* Iter53 P50 / Faza B v2 — Status general (calculat) — MULTI-SELECT */}
          <div className="md:col-span-2 lg:col-span-2">
            <Label className="text-xs" title="Status general calculat (8 valori). Selectează multiple pentru filtru OR.">
              Status general (multi-select)
            </Label>
            <div
              className="grid grid-cols-2 gap-1 mt-1 p-2 border rounded-md bg-white"
              data-testid="filter-general-status-multi"
            >
              {[
                "Nou", "În verificare BO", "În procesare PPC", "În curs de activare",
                "Activ", "Problemă", "Anulat", "Stornat",
              ].map((s) => {
                const arr = Array.isArray(value.general_status) ? value.general_status : [];
                const checked = arr.includes(s);
                return (
                  <label
                    key={s}
                    className={`flex items-center gap-1.5 px-2 py-1 rounded text-xs cursor-pointer border transition-colors ${
                      checked ? "bg-teal-50 border-teal-300 text-teal-900 font-medium" : "border-transparent hover:bg-slate-50"
                    }`}
                    data-testid={`filter-gs-option-${s}`}
                  >
                    <input
                      type="checkbox"
                      className="h-3.5 w-3.5"
                      checked={checked}
                      onChange={(e) => {
                        const next = e.target.checked
                          ? [...arr, s]
                          : arr.filter((x) => x !== s);
                        setField("general_status", next);
                      }}
                    />
                    <span className="truncate">{s}</span>
                  </label>
                );
              })}
            </div>
          </div>

          {/* Iter53 P50 — Conflict blocant */}
          <div>
            <Label className="text-xs" title="Vânzări cu cel puțin un conflict blocant între surse">
              Conflict blocant
            </Label>
            <Select value={value.has_blocking_conflict || ALL} onValueChange={(v) => setField("has_blocking_conflict", v)}>
              <SelectTrigger data-testid="filter-has-blocking-conflict" className={activeFilterClass(value.has_blocking_conflict)}>
                <SelectValue placeholder="Toate" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>Toate</SelectItem>
                <SelectItem value="true">Cu conflict</SelectItem>
                <SelectItem value="false">Fără conflict</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Faza B v2 — Stale filter */}
          <div>
            <Label className="text-xs" title="Vânzări unde datele-sursă au fost modificate după ultima calculare a Statusului General">
              Status neactualizat
            </Label>
            <Select value={value.is_stale || ALL} onValueChange={(v) => setField("is_stale", v)}>
              <SelectTrigger data-testid="filter-is-stale" className={activeFilterClass(value.is_stale)}>
                <SelectValue placeholder="Toate" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>Toate</SelectItem>
                <SelectItem value="true">Doar posibil neactualizate</SelectItem>
                <SelectItem value="false">Doar actualizate</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label className="text-xs" title="Stare contract canonică derivată din SF (sursa autoritativă pentru raportare comisionare)">
              Status Business (derivat SF)
            </Label>
            <Select value={value.business_status || ALL} onValueChange={(v) => setField("business_status", v)}>
              <SelectTrigger data-testid="filter-business-status" className={activeFilterClass(value.business_status)}>
                <SelectValue placeholder="Toate" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>Toate</SelectItem>
                <SelectItem value="draft">draft</SelectItem>
                <SelectItem value="signed">signed</SelectItem>
                <SelectItem value="submitted">submitted</SelectItem>
                <SelectItem value="processing">processing</SelectItem>
                <SelectItem value="validated">validated</SelectItem>
                <SelectItem value="activated">activated</SelectItem>
                <SelectItem value="terminated">terminated</SelectItem>
                <SelectItem value="cancelled">cancelled</SelectItem>
                <SelectItem value="rejected">rejected</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Iter52 P13 — Salesforce "Stare contract" (col V) — most important for commission decisions */}
          <div>
            <Label className="text-xs flex items-center gap-1">
              Status SF (Stare contract)
              <span className="text-[10px] text-indigo-600 font-semibold">⚡ comisionare</span>
            </Label>
            <Select value={value.sf_contract_state || ALL} onValueChange={(v) => setField("sf_contract_state", v)}>
              <SelectTrigger data-testid="filter-sf-contract-state" className={activeFilterClass(value.sf_contract_state)}>
                <SelectValue placeholder="Toate" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>Toate</SelectItem>
                <SelectItem value="Activat">Activat</SelectItem>
                <SelectItem value="Signed">Signed</SelectItem>
                <SelectItem value="Terminated">Terminated</SelectItem>
                <SelectItem value="__empty__">Fără status SF</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Iter52 P13d — SF Tip înregistrare caz (col J) filter */}
          <div>
            <Label className="text-xs flex items-center gap-1">
              Tip proces (SF)
              <span className="text-[10px] text-indigo-600 font-semibold">💰</span>
            </Label>
            <Select value={value.registration_type || ALL} onValueChange={(v) => setField("registration_type", v)}>
              <SelectTrigger data-testid="filter-registration-type" className={activeFilterClass(value.registration_type)}>
                <SelectValue placeholder="Toate" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>Toate</SelectItem>
                <SelectItem value="Acquisition Hard VAS">💰 Acquisition Hard VAS</SelectItem>
                <SelectItem value="Activare Energie Electrică">💰 Activare EE</SelectItem>
                <SelectItem value="Activare Gaz">💰 Activare Gaz</SelectItem>
                <SelectItem value="Activare Servicii">💰 Activare Servicii (VAS)</SelectItem>
                <SelectItem value="Switch-In Energie Electrică">💰 Switch-In EE</SelectItem>
                <SelectItem value="Switch-In Gaz">💰 Switch-In Gaz</SelectItem>
                <SelectItem value="ProductChange Electric">⊘ ProductChange EE</SelectItem>
                <SelectItem value="ProductChange Gas">⊘ ProductChange Gaz</SelectItem>
                <SelectItem value="Transfer Contract Energie Electrică">⊘ Transfer EE</SelectItem>
                <SelectItem value="Transfer Contract Gaz">⊘ Transfer Gaz</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Iter52 P13d — Commissionable / non-commissionable toggle */}
          <div>
            <Label className="text-xs flex items-center gap-1">
              Comisionabilitate
              <span className="text-[10px] text-indigo-600 font-semibold">💰</span>
            </Label>
            <Select value={value.commissionable || ALL} onValueChange={(v) => setField("commissionable", v)}>
              <SelectTrigger data-testid="filter-commissionable" className={activeFilterClass(value.commissionable)}>
                <SelectValue placeholder="Toate" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>Toate</SelectItem>
                <SelectItem value="true">💰 Doar comisionabile</SelectItem>
                <SelectItem value="false">⊘ Doar ne-comisionabile</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Iter52 P13h — Incomplete sales filter (missing product_kind OR offer_id) */}
          <div>
            <Label className="text-xs flex items-center gap-1">
              Completitudine
              <span className="text-[10px] text-amber-600 font-semibold">📋</span>
            </Label>
            <Select value={value.incomplete || ALL} onValueChange={(v) => setField("incomplete", v)}>
              <SelectTrigger data-testid="filter-incomplete" className={activeFilterClass(value.incomplete)}>
                <SelectValue placeholder="Toate" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>Toate</SelectItem>
                <SelectItem value="true">📋 Doar incomplete (fără tip / ofertă)</SelectItem>
                <SelectItem value="false">✓ Doar clasificate complet</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Iter52 P18 — Furnizor filter eliminat (gestionăm doar contracte PPC, deci nu are sens). */}

          {/* Source system */}
          <div>
            <Label className="text-xs">Sursă</Label>
            <Select value={value.source_system || ALL} onValueChange={(v) => setField("source_system", v)}>
              <SelectTrigger data-testid="filter-source" className={activeFilterClass(value.source_system)}>
                <SelectValue placeholder="Toate" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>Toate</SelectItem>
                <SelectItem value="manual">Manual</SelectItem>
                <SelectItem value="easy">Easy App</SelectItem>
                <SelectItem value="salesforce">Salesforce</SelectItem>
                <SelectItem value="crm_legacy">CRM Vechi</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Customer type */}
          <div>
            <Label className="text-xs">Tip Client</Label>
            <Select value={value.customer_type || ALL} onValueChange={(v) => setField("customer_type", v)}>
              <SelectTrigger data-testid="filter-customer-type" className={activeFilterClass(value.customer_type)}>
                <SelectValue placeholder="Toate" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>Toate</SelectItem>
                <SelectItem value="PF">Persoană Fizică</SelectItem>
                <SelectItem value="PJ">Persoană Juridică</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Agency */}
          <div>
            <Label className="text-xs">Agenție</Label>
            <Select value={value.agency_id || ALL} onValueChange={(v) => setField("agency_id", v)}>
              <SelectTrigger data-testid="filter-agency" className={activeFilterClass(value.agency_id)}>
                <SelectValue placeholder="Toate" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>Toate</SelectItem>
                {agencies.map((a) => (
                  <SelectItem key={a.id} value={a.id}>
                    {a.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Agent comercial — Iter62o: căutare + prefiltrare după agenția selectată */}
          <div>
            <Label className="text-xs">Agent comercial</Label>
            {value.commercial_agent_id ? (
              <div
                className="flex items-center justify-between gap-2 h-10 px-3 rounded-md border border-emerald-300 bg-emerald-50 text-sm"
                data-testid="filter-commercial-agent-selected"
              >
                <span className="truncate">
                  {(commAgents.find((a) => a.id === value.commercial_agent_id) || {}).full_name || "Agent selectat"}
                </span>
                <button
                  type="button"
                  className="text-slate-500 hover:text-red-600 font-bold"
                  onClick={() => { setField("commercial_agent_id", ""); setAgentSearch(""); }}
                  data-testid="filter-commercial-agent-clear"
                  title="Șterge selecția"
                >
                  ×
                </button>
              </div>
            ) : (
              <div className="relative">
                <Input
                  placeholder={value.agency_id ? "Caută în agenția selectată..." : "Caută agent..."}
                  value={agentSearch}
                  onChange={(e) => setAgentSearch(e.target.value)}
                  data-testid="filter-commercial-agent-search"
                />
                {agentSearch.trim().length >= 1 && (
                  <div className="absolute z-50 mt-1 w-full max-h-56 overflow-auto rounded-md border border-slate-200 bg-white shadow-lg">
                    {commAgents
                      .filter((a) => !value.agency_id || a.agency_id === value.agency_id)
                      .filter((a) => (a.full_name || "").toLowerCase().includes(agentSearch.trim().toLowerCase()))
                      .slice(0, 50)
                      .map((a) => (
                        <button
                          key={a.id}
                          type="button"
                          className="w-full text-left px-3 py-1.5 text-sm hover:bg-slate-100"
                          onClick={() => { setField("commercial_agent_id", a.id); setAgentSearch(""); }}
                          data-testid={`filter-commercial-agent-option-${a.id}`}
                        >
                          {a.full_name}
                          {a.agency_name && <span className="text-xs text-slate-400"> · {a.agency_name}</span>}
                        </button>
                      ))}
                    {commAgents
                      .filter((a) => !value.agency_id || a.agency_id === value.agency_id)
                      .filter((a) => (a.full_name || "").toLowerCase().includes(agentSearch.trim().toLowerCase())).length === 0 && (
                      <div className="px-3 py-2 text-sm text-slate-400">Niciun agent găsit</div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Agent (creator) */}
          <div>
            <Label className="text-xs" title="User-ul care a creat/încărcat vânzarea în CRM — diferit de agentul comercial care a semnat">Încărcat de (user)</Label>
            <Select value={value.agent_id || ALL} onValueChange={(v) => setField("agent_id", v)}>
              <SelectTrigger data-testid="filter-agent" className={activeFilterClass(value.agent_id)}>
                <SelectValue placeholder="Toți" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>Toți</SelectItem>
                {agents.map((a) => (
                  <SelectItem key={a.id} value={a.id}>
                    {a.name || a.email}
                    {a.agent_code ? ` (${a.agent_code})` : ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Product Kind (canonical) — Iter52 P18: was empty product list */}
          <div>
            <Label className="text-xs">Tip Produs</Label>
            <Select value={value.product_kind || ALL} onValueChange={(v) => setField("product_kind", v)}>
              <SelectTrigger data-testid="filter-product-kind" className={activeFilterClass(value.product_kind)}>
                <SelectValue placeholder="Toate" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>Toate</SelectItem>
                <SelectItem value="EE">EE (Energie Electrică)</SelectItem>
                <SelectItem value="Gaz">Gaz</SelectItem>
                <SelectItem value="VAS">VAS</SelectItem>
                <SelectItem value="HVAS">HVAS</SelectItem>
                <SelectItem value="Combo EE + VAS">Combo EE + VAS</SelectItem>
                <SelectItem value="Combo Gaz + VAS">Combo Gaz + VAS</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Offer — Iter52 P18: searchable combobox + filtered by product_kind */}
          <div>
            <Label className="text-xs">Ofertă</Label>
            <OfferFilterCombobox
              offers={offers}
              value={value.offer_id || ""}
              onChange={(v) => setField("offer_id", v)}
              productKindFilter={value.product_kind}
            />
          </div>

          {/* Date from */}
          <div>
            <Label className="text-xs">Data semnării de la</Label>
            <Input
              type="date"
              value={value.signed_date_from || ""}
              onChange={(e) => setField("signed_date_from", e.target.value)}
              data-testid="filter-signed-from"
              className={activeFilterClass(value.signed_date_from)}
            />
          </div>

          {/* Date to */}
          <div>
            <Label className="text-xs">până la</Label>
            <Input
              type="date"
              value={value.signed_date_to || ""}
              onChange={(e) => setField("signed_date_to", e.target.value)}
              data-testid="filter-signed-to"
              className={activeFilterClass(value.signed_date_to)}
            />
          </div>

          {/* needs_reconciliation toggle (admin/backoffice only) */}
          {showReconciliation && (
            <div>
              <Label className="text-xs flex items-center gap-1">
                <AlertTriangle className="h-3 w-3 text-amber-500" />
                Reconciliere
              </Label>
              <Select
                value={value.needs_reconciliation || ALL}
                onValueChange={(v) => setField("needs_reconciliation", v)}
              >
                <SelectTrigger data-testid="filter-reconciliation" className={activeFilterClass(value.needs_reconciliation)}>
                  <SelectValue placeholder="Toate" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={ALL}>Toate</SelectItem>
                  <SelectItem value="true">Doar neasociate</SelectItem>
                  <SelectItem value="false">Doar asociate</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Iter36 — Returnare filter */}
          <div>
            <Label className="text-xs">Returnare</Label>
            <Select
              value={value.returned || ALL}
              onValueChange={(v) => setField("returned", v)}
            >
              <SelectTrigger data-testid="filter-returned" className={activeFilterClass(value.returned)}>
                <SelectValue placeholder="Toate" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>Toate</SelectItem>
                <SelectItem value="true">Doar returnate</SelectItem>
                <SelectItem value="false">Doar non-returnate</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Iter36 — Comisionare filter */}
          <div>
            <Label className="text-xs">Comisionare</Label>
            <Select
              value={value.commissioned || ALL}
              onValueChange={(v) => setField("commissioned", v)}
            >
              <SelectTrigger data-testid="filter-commissioned" className={activeFilterClass(value.commissioned)}>
                <SelectValue placeholder="Toate" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>Toate</SelectItem>
                <SelectItem value="true">Doar comisionate</SelectItem>
                <SelectItem value="false">Doar necomisionate</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Activation days min (contracts only) */}
          {showActivationDays && (
            <div>
              <Label className="text-xs">În Activare ≥ (zile)</Label>
              <Input
                type="number"
                min={0}
                placeholder="ex: 7"
                value={value.activation_days_min || ""}
                onChange={(e) => setField("activation_days_min", e.target.value)}
                data-testid="filter-activation-days"
                className={activeFilterClass(value.activation_days_min)}
              />
            </div>
          )}

          {/* Iter23/P14q/P18 — Supplier-side (PPC, fost ENEL). Enum canonic SF. */}
          <div>
            <Label className="text-xs">Status furnizor (PPC)</Label>
            <Select
              value={value.supplier_status || ALL}
              onValueChange={(v) => setField("supplier_status", v)}
            >
              <SelectTrigger data-testid="filter-supplier-status" className={activeFilterClass(value.supplier_status)}>
                <SelectValue placeholder="Toate" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>Toate</SelectItem>
                <SelectItem value="Signed">Signed (semnat)</SelectItem>
                <SelectItem value="Activated">Activated (activ)</SelectItem>
                <SelectItem value="Terminated">Terminated (reziliat)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {/* Iter52 P18 — "OK furnizor" filtru eliminat (vezi P14q: câmpurile supplier_ok/ko sunt deprecated). */}
          <div>
            <Label className="text-xs">CSD de la</Label>
            <Input
              type="date"
              value={value.csd_date_from || ""}
              onChange={(e) => setField("csd_date_from", e.target.value)}
              data-testid="filter-csd-from"
              className={activeFilterClass(value.csd_date_from)}
            />
          </div>
          <div>
            <Label className="text-xs">CSD până la</Label>
            <Input
              type="date"
              value={value.csd_date_to || ""}
              onChange={(e) => setField("csd_date_to", e.target.value)}
              data-testid="filter-csd-to"
              className={activeFilterClass(value.csd_date_to)}
            />
          </div>

          {/* Iter62k2 — filtrele „Document Validation de la/până la" au fost eliminate:
              processing_cases.document_validation_date are 0 valori în DB (coloana
              nu vine în exporturile SF reale) — filtru mort, nu putea returna nimic. */}
          {/* Iter62k — filtrul „Request Confirmation (CSD SF)" a fost eliminat:
              era redundant cu „CSD de la/până la" (aceeași coloană-sursă SF
              „Request Confirmation Date"; csd_date are acoperire dublă). */}
          <div>
            <Label className="text-xs">Ultima modificare de (SF)</Label>
            <Input
              type="text"
              placeholder="ex. Cristi Lozinca"
              value={value.sf_last_modified_by || ""}
              onChange={(e) => setField("sf_last_modified_by", e.target.value)}
              data-testid="filter-sf-last-modified-by"
              className={activeFilterClass(value.sf_last_modified_by)}
            />
          </div>

          {/* Sort by + dir */}
          <div>
            <Label className="text-xs">Sortează după</Label>
            <Select value={value.sort_by || "created_at"} onValueChange={(v) => setField("sort_by", v)}>
              <SelectTrigger data-testid="filter-sort-by">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="created_at">Data creării</SelectItem>
                <SelectItem value="signed_date">Data semnării</SelectItem>
                <SelectItem value="pod">POD</SelectItem>
                <SelectItem value="supplier">Furnizor</SelectItem>
                <SelectItem value="activation_date">Data activării</SelectItem>
                {/* Faza B v2 — sort by general status */}
                <SelectItem value="general_status">Status general</SelectItem>
                <SelectItem value="general_status_calculated_at">Ultima calculare status</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label className="text-xs">Ordine</Label>
            <Select value={value.sort_dir || "desc"} onValueChange={(v) => setField("sort_dir", v)}>
              <SelectTrigger data-testid="filter-sort-dir">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="desc">Descrescător</SelectItem>
                <SelectItem value="asc">Crescător</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export const useAdvancedFilters = (storageKey) => {
  const [filters, setFilters] = useState(() => {
    if (storageKey) {
      try {
        const stored = sessionStorage.getItem(storageKey);
        if (stored) return { ...EMPTY, ...JSON.parse(stored) };
      } catch {
        // ignore
      }
    }
    return { ...EMPTY };
  });
  return [filters, setFilters];
};

// Helper to convert filter state into URL search params
export const filtersToSearchParams = (filters) => {
  const params = new URLSearchParams();
  Object.entries(filters).forEach(([key, val]) => {
    if (val === "" || val === null || val === undefined) return;
    params.set(key, val);
  });
  return params;
};

export default AdvancedFilters;
